import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MaterialRequestPanel from "@/components/MaterialRequestPanel";
import NotificationsPanel from "@/components/NotificationsPanel";
import MenuViewer from "@/components/MenuViewer";
import ItemSearchSelect from "@/components/ItemSearchSelect";
import { useAuth } from "@/context/AuthContext";
import HistoryStamps from "@/components/HistoryStamps";
import { todayStr, fmtDay, nowHM, dateStrIST } from "@/lib/utils";
import { Plus, Trash2, Send, Clock, UtensilsCrossed, Loader2, Package, Pencil, PackageSearch, PackageCheck, Bell, RotateCcw, BookOpen } from "lucide-react";
import { toast } from "sonner";

const TIME_CHIPS = [
  { label: "Breakfast" },
  { label: "Lunch" },
  { label: "Snacks" },
  { label: "Dinner" },
];

const MEAL_DEFAULT_TIMES = { Breakfast: "08:00", Lunch: "12:30", Snacks: "16:30", Dinner: "19:30" };

const tomorrowStr = () => dateStrIST(Date.now() + 86400000);
const mealPassed = (date, label) => date === todayStr() && MEAL_DEFAULT_TIMES[label] <= nowHM();
const isPast = (date, clock) => date < todayStr() || (date === todayStr() && (clock || "") <= nowHM());
const closestSlot = () => {
  const next = TIME_CHIPS.find((c) => MEAL_DEFAULT_TIMES[c.label] > nowHM());
  return next ? { date: todayStr(), meal: next.label } : { date: tomorrowStr(), meal: "Breakfast" };
};

const fmtClock = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function CafeteriaView() {
  const { user } = useAuth();
  const [cafeterias, setCafeterias] = useState([]);
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [cafeteriaId, setCafeteriaId] = useState("");
  const [baseKitchen, setBaseKitchen] = useState("MIHAN");
  const [initSlot] = useState(closestSlot);
  const [deliveryDate, setDeliveryDate] = useState(initSlot.date);
  const [deliveryTime, setDeliveryTime] = useState(initSlot.meal);
  const [deliveryClock, setDeliveryClock] = useState(MEAL_DEFAULT_TIMES[initSlot.meal]);
  const [remarks, setRemarks] = useState("");
  const [rows, setRows] = useState([{ item_id: "", qty: "" }]);

  const [nowTick, setNowTick] = useState(Date.now());
  const [editOrder, setEditOrder] = useState(null);
  const [editCaf, setEditCaf] = useState("");
  const [editBase, setEditBase] = useState("MIHAN");
  const [editDate, setEditDate] = useState(todayStr());
  const [editTime, setEditTime] = useState("Lunch");
  const [editClock, setEditClock] = useState(MEAL_DEFAULT_TIMES.Lunch);
  const [editRemarks, setEditRemarks] = useState("");
  const [editRows, setEditRows] = useState([{ item_id: "", qty: "" }]);
  const [editSaving, setEditSaving] = useState(false);

  const [recvOrder, setRecvOrder] = useState(null);
  const [recvQty, setRecvQty] = useState({});
  const [recvComment, setRecvComment] = useState("");
  const [recvSaving, setRecvSaving] = useState(false);

  const openReceive = (o) => {
    setRecvOrder(o);
    setRecvQty(Object.fromEntries(o.items.map((it) => [it.item_id, it.dispatched_qty ?? it.qty])));
    setRecvComment("");
  };

  const saveReceive = async () => {
    setRecvSaving(true);
    try {
      await api.patch(`/orders/${recvOrder.id}/receive`, {
        items: recvOrder.items.map((it) => ({ item_id: it.item_id, qty: Number(recvQty[it.item_id] || 0) })),
        comment: recvComment,
      });
      toast.success("Order marked received");
      setRecvOrder(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRecvSaving(false);
    }
  };

  const loadAll = async () => {
    try {
      const [c, i, o] = await Promise.all([
        api.get("/cafeterias"),
        api.get("/items"),
        api.get("/orders"),
      ]);
      const active = c.data.filter((x) => x.active !== false);
      setCafeterias(active);
      setItems(i.data.filter((x) => x.active !== false));
      setOrders(o.data);
      if (user?.role === "cafeteria") {
        const mine = active.find((x) => x.name.trim() === (user.location || "").trim());
        if (mine) {
          setCafeteriaId(mine.id);
          if (mine.default_base_kitchen) setBaseKitchen(mine.default_base_kitchen);
        }
        else setCafeteriaId("");
      } else if (active.length && !cafeteriaId) setCafeteriaId(active[0].id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(() => {
      setNowTick(Date.now());
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const itemById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  const changeDate = (v) => {
    setDeliveryDate(v);
    if (!isPast(v, deliveryClock)) return;
    const s = closestSlot();
    if (s.date !== v) {
      setDeliveryDate(s.date);
      toast.info(v < todayStr() ? "Past dates can't be selected — moved to the next available slot" : "All of today's meal times have passed — moved to tomorrow");
    }
    setDeliveryTime(s.meal);
    setDeliveryClock(MEAL_DEFAULT_TIMES[s.meal]);
  };

  const lastMealOrder = useMemo(
    () => orders.find((o) => o.delivery_time === deliveryTime && o.cafeteria_id === cafeteriaId),
    [orders, deliveryTime, cafeteriaId]
  );

  const repeatLast = () => {
    const o = lastMealOrder;
    const available = o.items.filter((it) => itemById[it.item_id]);
    if (!available.length) return toast.error("None of those items are available any more");
    setRows(available.map((it) => ({ item_id: it.item_id, qty: String(it.qty) })));
    setBaseKitchen(o.base_kitchen);
    if (o.delivery_exact_time && !isPast(deliveryDate, o.delivery_exact_time)) setDeliveryClock(o.delivery_exact_time);
    setRemarks(o.remarks || "");
    const missing = o.items.length - available.length;
    toast.success(`Filled from last ${deliveryTime} order${missing ? ` (${missing} item(s) no longer available)` : ""}`);
  };

  const estimated = useMemo(
    () =>
      rows.reduce((sum, r) => {
        const it = itemById[r.item_id];
        return sum + (it ? Number(r.qty || 0) * it.rate : 0);
      }, 0),
    [rows, itemById]
  );

  const addRow = () => setRows([...rows, { item_id: "", qty: "" }]);
  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));
  const updateRow = (idx, patch) => setRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const canEdit = (o) =>
    o.status === "Preparing" && o.edit_deadline && nowTick < Date.parse(o.edit_deadline);

  const openEdit = (o) => {
    setEditOrder(o);
    setEditCaf(o.cafeteria_id);
    setEditBase(o.base_kitchen);
    setEditTime(o.delivery_time);
    setEditDate(o.delivery_date || todayStr());
    setEditClock(o.delivery_exact_time || MEAL_DEFAULT_TIMES[o.delivery_time] || "12:30");
    setEditRemarks(o.remarks || "");
    setEditRows(o.items.map((it) => ({ item_id: it.item_id, qty: String(it.qty) })));
  };

  const saveEdit = async () => {
    const payloadItems = editRows
      .filter((r) => r.item_id && Number(r.qty) > 0)
      .map((r) => {
        const it = itemById[r.item_id];
        return { item_id: it.id, item_name: it.name, qty: Number(r.qty), unit: it.unit, rate: it.rate };
      });
    if (!payloadItems.length) return toast.error("Add at least one item with quantity");
    setEditSaving(true);
    try {
      if (!editClock) return toast.error("Select the time of delivery");
      if (isPast(editDate, editClock)) return toast.error("Delivery time has already passed — choose a later time or date");
      await api.put(`/orders/${editOrder.id}`, {
        cafeteria_id: editCaf,
        base_kitchen: editBase,
        delivery_date: editDate,
        delivery_time: editTime,
        delivery_exact_time: editClock,
        items: payloadItems,
        remarks: editRemarks,
      });
      toast.success("Order updated");
      setEditOrder(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  const submit = async () => {
    const payloadItems = rows
      .filter((r) => r.item_id && Number(r.qty) > 0)
      .map((r) => {
        const it = itemById[r.item_id];
        return { item_id: it.id, item_name: it.name, qty: Number(r.qty), unit: it.unit, rate: it.rate };
      });
    if (!cafeteriaId) return toast.error("Select a cafeteria");
    if (!deliveryDate) return toast.error("Select the delivery date");
    if (!deliveryTime) return toast.error("Select a meal");
    if (!deliveryClock) return toast.error("Select the time of delivery");
    if (isPast(deliveryDate, deliveryClock)) return toast.error("Delivery time has already passed — choose a later time or date");
    if (!payloadItems.length) return toast.error("Add at least one item with quantity");
    setLoading(true);
    try {
      await api.post("/orders", {
        cafeteria_id: cafeteriaId,
        base_kitchen: baseKitchen,
        delivery_date: deliveryDate,
        delivery_time: deliveryTime,
        delivery_exact_time: deliveryClock,
        items: payloadItems,
        remarks,
      });
      toast.success("Order placed and sent to kitchen");
      setRows([{ item_id: "", qty: "" }]);
      setRemarks("");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="order">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="order" data-testid="cafeteria-tab-order" className="gap-2">
              <UtensilsCrossed className="h-4 w-4" /> Place Order
            </TabsTrigger>
            <TabsTrigger value="materials" data-testid="cafeteria-tab-materials" className="gap-2">
              <PackageSearch className="h-4 w-4" /> Request Material
            </TabsTrigger>
            <TabsTrigger value="menu" data-testid="cafeteria-tab-menu" className="gap-2">
              <BookOpen className="h-4 w-4" /> Menu
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="cafeteria-tab-notifications" className="gap-2">
              <Bell className="h-4 w-4" /> Notifications
            </TabsTrigger>
          </TabsList>

          <TabsContent value="order" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Order builder */}
        <section className="lg:col-span-7 fade-up">
          <div className="flex items-center gap-2 mb-1">
            <UtensilsCrossed className="h-5 w-5 text-primary" />
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Place an Order</h1>
          </div>
          <p className="text-muted-foreground mb-6">Request items from a Base Kitchen for your counter.</p>

          <Card className="p-6 space-y-6 border-orange-100 dark:border-slate-800">
            {/* Cafeteria */}
            <div className="space-y-2">
              <Label>Cafeteria</Label>
              <Select value={cafeteriaId} onValueChange={(v) => {
                setCafeteriaId(v);
                const caf = cafeterias.find((x) => x.id === v);
                if (caf?.default_base_kitchen) setBaseKitchen(caf.default_base_kitchen);
              }} disabled={user?.role === "cafeteria"}>
                <SelectTrigger data-testid="order-cafeteria-select"><SelectValue placeholder="Select cafeteria" /></SelectTrigger>
                <SelectContent>
                  {cafeterias.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {user?.role === "cafeteria" && (
                cafeteriaId ? (
                  <p className="text-xs text-muted-foreground" data-testid="order-cafeteria-locked-note">Locked to the cafeteria mapped to your login.</p>
                ) : (
                  <p className="text-xs text-destructive" data-testid="order-cafeteria-unmapped-note">Your login is not mapped to an active cafeteria. Ask your admin to assign one.</p>
                )
              )}
            </div>

            {/* Base kitchen */}
            <div className="space-y-2">
              <Label>Base Kitchen</Label>
              <div className="grid grid-cols-2 gap-3">
                {["MIHAN", "MANEWADA"].map((bk) => (
                  <button
                    key={bk}
                    type="button"
                    data-testid={`order-base-kitchen-${bk}`}
                    onClick={() => setBaseKitchen(bk)}
                    className={`rounded-xl border-2 p-4 text-left transition-colors ${
                      baseKitchen === bk
                        ? "border-primary bg-accent"
                        : "border-border hover:border-primary/40"
                    }`}
                  >
                    <span className="font-heading font-bold text-lg">{bk}</span>
                    <span className="block text-xs text-muted-foreground">Base Kitchen Hub</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Delivery date */}
            <div className="space-y-2">
              <Label>Delivery Date</Label>
              <Input
                type="date"
                min={todayStr()}
                value={deliveryDate}
                onChange={(e) => changeDate(e.target.value)}
                className="w-48 font-mono"
                data-testid="order-delivery-date-input"
              />
            </div>

            {/* Delivery time */}
            <div className="space-y-2">
              <Label>Meal</Label>
              <div className="flex flex-wrap items-center gap-2">
                {TIME_CHIPS.map((chip) => (
                  <button
                    key={chip.label}
                    type="button"
                    data-testid={`order-time-chip-${chip.label}`}
                    disabled={mealPassed(deliveryDate, chip.label)}
                    title={mealPassed(deliveryDate, chip.label) ? `${chip.label} time has passed for today` : undefined}
                    onClick={() => {
                      setDeliveryTime(chip.label);
                      setDeliveryClock(MEAL_DEFAULT_TIMES[chip.label] || "");
                    }}
                    className={`px-4 py-1.5 rounded-full text-sm border transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:line-through ${
                      deliveryTime === chip.label
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:bg-secondary"
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Label className="text-muted-foreground font-normal">Time of Delivery</Label>
                <div className="flex items-center gap-2 rounded-full border border-border px-3 py-1.5 bg-background hover:border-primary/40 transition-colors">
                  <Clock className="h-4 w-4 text-primary" />
                  <input
                    type="time"
                    data-testid="order-delivery-time-input"
                    value={deliveryClock}
                    min={deliveryDate === todayStr() ? nowHM() : undefined}
                    onChange={(e) => setDeliveryClock(e.target.value)}
                    className="bg-transparent text-sm font-mono outline-none cursor-pointer"
                  />
                </div>
                {deliveryClock && (
                  <span className="text-xs text-muted-foreground" data-testid="order-delivery-time-display">
                    {deliveryTime} at {fmtClock(deliveryClock)}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-3" data-testid="order-items-section">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label>Items</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={repeatLast}
                  disabled={!lastMealOrder}
                  title={lastMealOrder ? `Copy items from the ${deliveryTime} order placed ${new Date(lastMealOrder.created_at).toLocaleDateString("en-IN")}` : `No previous ${deliveryTime} order`}
                  data-testid="order-repeat-last-button"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Repeat last {deliveryTime} order
                </Button>
              </div>
              {rows.map((row, idx) => {
                const it = itemById[row.item_id];
                return (
                  <div key={idx} className="flex flex-wrap sm:flex-nowrap items-end gap-2" data-testid="order-item-row">
                    <div className="basis-full sm:basis-0 sm:flex-1 min-w-0">
                      <ItemSearchSelect
                        items={items}
                        value={row.item_id}
                        onChange={(v) => updateRow(idx, { item_id: v })}
                        testid="order-item-select"
                        fmtMoney={fmtMoney}
                      />
                    </div>
                    <div className="flex-1 sm:flex-none sm:w-24">
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        placeholder="Qty"
                        data-testid="order-item-qty-input"
                        value={row.qty}
                        onChange={(e) => updateRow(idx, { qty: e.target.value })}
                      />
                    </div>
                    <Badge variant="outline" className="h-10 px-3 min-w-[52px] justify-center" data-testid="order-item-unit-badge">
                      {it ? it.unit : "—"}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid="order-item-remove-button"
                      onClick={() => removeRow(idx)}
                      disabled={rows.length === 1}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                );
              })}
              <Button variant="outline" size="sm" onClick={addRow} data-testid="order-add-item-button" className="gap-2">
                <Plus className="h-4 w-4" /> Add item
              </Button>
            </div>

            {/* Remarks */}
            <div className="space-y-2">
              <Label>Remarks (optional)</Label>
              <Textarea
                data-testid="order-remarks-textarea"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="e.g. Extra mild gravy, pack in 5kg insulated containers"
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div>
                <p className="text-xs uppercase font-mono tracking-widest text-muted-foreground">Est. material value</p>
                <p className="font-mono text-2xl font-extrabold text-primary" data-testid="order-estimated-value">{fmtMoney(estimated)}</p>
              </div>
              <Button onClick={submit} disabled={loading} className="gap-2 h-11 px-6" data-testid="order-submit-button">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
                Place Order
              </Button>
            </div>
          </Card>
        </section>

        {/* Order tracker */}
        <section className="lg:col-span-5 mobile-compact" data-testid="cafeteria-orders-section">
          <h2 className="font-heading text-xl font-bold tracking-tight mb-4">Cafeteria Orders</h2>
          <div className="space-y-3" data-testid="cafeteria-orders-list">
            {orders.length === 0 && (
              <Card className="p-8 text-center text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
                No orders yet. Place your first order.
              </Card>
            )}
            {orders.map((o) => (
              <Card key={o.id} className="p-4 border-orange-100 dark:border-slate-800 fade-up" data-testid="cafeteria-order-card">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="font-semibold">{o.cafeteria_name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                  <StatusBadge status={o.status} testid="cafeteria-order-status-badge" />
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <Badge variant="secondary">{o.base_kitchen}</Badge>
                  <Badge variant="secondary" data-testid="cafeteria-order-delivery-badge">Deliver {o.delivery_date ? `${fmtDay(o.delivery_date)} · ` : ""}{o.delivery_time}{o.delivery_exact_time ? ` · ${fmtClock(o.delivery_exact_time)}` : ""}</Badge>
                </div>
                <ul className="text-sm space-y-0.5 mb-2">
                  {o.status === "Dispatched" || o.status === "Delivered" ? (
                    <li className="grid grid-cols-[1fr_auto_auto_auto] gap-2 text-[10px] uppercase font-mono tracking-wider text-muted-foreground pb-0.5 border-b border-border">
                      <span>Item</span><span className="text-right">Ord</span><span className="text-right">Disp</span><span className="text-right">Rcvd</span>
                    </li>
                  ) : null}
                  {o.items.map((it, i) => (
                    (o.status === "Dispatched" || o.status === "Delivered") ? (
                      <li key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                        <span className="truncate">{it.item_name}</span>
                        <span className="font-mono text-muted-foreground text-right">{it.qty}</span>
                        <span className="font-mono font-semibold text-right">{it.dispatched_qty ?? 0}</span>
                        <span className="font-mono text-right text-emerald-700 dark:text-emerald-400">{o.status === "Delivered" ? (it.received_qty ?? 0) : "—"}</span>
                      </li>
                    ) : (
                      <li key={i} className="flex justify-between">
                        <span>{it.item_name}</span>
                        <span className="font-mono text-muted-foreground">{it.qty} {it.unit}</span>
                      </li>
                    )
                  ))}
                </ul>
                {o.remarks && <p className="text-xs italic text-muted-foreground border-t pt-2">“{o.remarks}”</p>}
                {o.received_comment && <p className="text-xs text-rose-700 dark:text-rose-300 border-t pt-2 mt-1"><span className="font-semibold">Receipt note:</span> “{o.received_comment}”</p>}
                <HistoryStamps history={o.history} fallbackAction="Placed" fallbackName={o.placed_by_name} fallbackAt={o.created_at} testid="cafeteria-order-history" />
                <div className="flex items-center justify-between mt-3">
                  {o.status === "Preparing" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      data-testid="order-edit-button"
                      disabled={!canEdit(o)}
                      title={canEdit(o) ? "Edit this order" : "Editing closed — within 2 hours of delivery time"}
                      onClick={() => openEdit(o)}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                  )}
                  {o.status === "Dispatched" && (
                    <Button size="sm" className="gap-1.5" data-testid="order-receive-button" onClick={() => openReceive(o)}>
                      <PackageCheck className="h-3.5 w-3.5" /> Received
                    </Button>
                  )}
                  {o.status === "Delivered" && (
                    <span className="text-xs text-emerald-600 font-medium flex items-center gap-1"><PackageCheck className="h-3.5 w-3.5" /> Received</span>
                  )}
                  <p className="font-mono text-sm font-bold text-primary">{fmtMoney(o.total_value)}</p>
                </div>
              </Card>
            ))}
          </div>
        </section>
          </TabsContent>

          <TabsContent value="materials">
            <MaterialRequestPanel />
          </TabsContent>

          <TabsContent value="menu">
            <MenuViewer title={(user?.location || "").trim()} showCafeterias={false} />
          </TabsContent>

          <TabsContent value="notifications">
            <NotificationsPanel />
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit order dialog */}
      <Dialog open={!!editOrder} onOpenChange={(v) => !v && setEditOrder(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Order</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label>Cafeteria</Label>
              <Select value={editCaf} onValueChange={setEditCaf} disabled={user?.role === "cafeteria"}>
                <SelectTrigger data-testid="edit-cafeteria-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cafeterias.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Base Kitchen</Label>
              <div className="grid grid-cols-2 gap-3">
                {["MIHAN", "MANEWADA"].map((bk) => (
                  <button
                    key={bk}
                    type="button"
                    data-testid={`edit-base-kitchen-${bk}`}
                    onClick={() => setEditBase(bk)}
                    className={`rounded-xl border-2 p-3 text-left transition-colors ${editBase === bk ? "border-primary bg-accent" : "border-border hover:border-primary/40"}`}
                  >
                    <span className="font-heading font-bold">{bk}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Delivery Date</Label>
              <Input type="date" min={todayStr()} value={editDate} onChange={(e) => setEditDate(e.target.value)} className="w-48 font-mono" data-testid="edit-delivery-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Meal</Label>
              <div className="flex flex-wrap gap-2">
                {TIME_CHIPS.map((chip) => (
                  <button
                    key={chip.label}
                    type="button"
                    data-testid={`edit-time-chip-${chip.label}`}
                    disabled={mealPassed(editDate, chip.label)}
                    onClick={() => {
                      setEditTime(chip.label);
                      setEditClock(MEAL_DEFAULT_TIMES[chip.label] || "");
                    }}
                    className={`px-4 py-1.5 rounded-full text-sm border transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:line-through ${editTime === chip.label ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Label className="text-muted-foreground font-normal">Time of Delivery</Label>
                <div className="flex items-center gap-2 rounded-full border border-border px-3 py-1.5 bg-background hover:border-primary/40 transition-colors">
                  <Clock className="h-4 w-4 text-primary" />
                  <input
                    type="time"
                    data-testid="edit-delivery-time-input"
                    value={editClock}
                    min={editDate === todayStr() ? nowHM() : undefined}
                    onChange={(e) => setEditClock(e.target.value)}
                    className="bg-transparent text-sm font-mono outline-none cursor-pointer"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Label>Items</Label>
              {editRows.map((row, idx) => {
                const it = itemById[row.item_id];
                return (
                  <div key={idx} className="flex flex-wrap sm:flex-nowrap items-end gap-2" data-testid="edit-item-row">
                    <div className="basis-full sm:basis-0 sm:flex-1 min-w-0">
                      <ItemSearchSelect
                        items={items}
                        value={row.item_id}
                        onChange={(v) => setEditRows(editRows.map((r, i) => (i === idx ? { ...r, item_id: v } : r)))}
                        testid="edit-item-select"
                        fmtMoney={fmtMoney}
                      />
                    </div>
                    <div className="flex-1 sm:flex-none sm:w-24">
                      <Input type="number" min="0" step="0.5" placeholder="Qty" data-testid="edit-item-qty-input"
                        value={row.qty}
                        onChange={(e) => setEditRows(editRows.map((r, i) => (i === idx ? { ...r, qty: e.target.value } : r)))} />
                    </div>
                    <Badge variant="outline" className="h-10 px-3 min-w-[52px] justify-center">{it ? it.unit : "—"}</Badge>
                    <Button variant="ghost" size="icon" onClick={() => setEditRows(editRows.filter((_, i) => i !== idx))} disabled={editRows.length === 1}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                );
              })}
              <Button variant="outline" size="sm" className="gap-2" data-testid="edit-add-item-button"
                onClick={() => setEditRows([...editRows, { item_id: "", qty: "" }])}>
                <Plus className="h-4 w-4" /> Add item
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea data-testid="edit-remarks-textarea" value={editRemarks} onChange={(e) => setEditRemarks(e.target.value)} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} disabled={editSaving} className="gap-2" data-testid="edit-save-button">
              {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive order dialog */}
      <Dialog open={!!recvOrder} onOpenChange={(v) => !v && setRecvOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Receive Order</DialogTitle>
            <DialogDescription>Enter how much of each item you actually received, and note any problem with the food.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-[45vh] overflow-y-auto">
            <div className="grid grid-cols-[1fr_auto_auto] gap-2 text-[10px] uppercase font-mono tracking-wider text-muted-foreground pb-1 border-b border-border sticky top-0 bg-card">
              <span>Item</span><span className="text-right">Dispatched</span><span className="text-right w-24">Received Qty</span>
            </div>
            {recvOrder?.items.map((it, i) => {
              const disp = it.dispatched_qty ?? it.qty;
              return (
                <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center py-1" data-testid="order-receive-item-row">
                  <span className="truncate text-sm">{it.item_name} <span className="text-muted-foreground">({it.unit})</span></span>
                  <span className="font-mono text-sm text-muted-foreground text-right">{disp}</span>
                  <Input
                    type="number" min="0" max={disp} step="0.5"
                    className="h-8 w-24 text-right font-mono"
                    data-testid="order-receive-qty-input"
                    value={recvQty[it.item_id] ?? ""}
                    onChange={(e) => setRecvQty({ ...recvQty, [it.item_id]: e.target.value })}
                  />
                </div>
              );
            })}
          </div>
          <div className="space-y-2">
            <Label>Comment (any problem with the food received?)</Label>
            <Textarea data-testid="order-receive-comment" value={recvComment} onChange={(e) => setRecvComment(e.target.value)} rows={2} placeholder="e.g. Paneer gravy was cold, 2 kg short on rice" />
          </div>
          <DialogFooter>
            <Button onClick={saveReceive} disabled={recvSaving} className="gap-2" data-testid="order-receive-confirm-button">
              {recvSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />} Confirm Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
